/**
 * Digit OTP inputs with paste, arrow, and auto-advance support.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

/** Props for {@link OtpInput}. */
interface OtpInputProps {
  length?: number;
  value: string;
  onChange: (_value: string) => void;
  onComplete?: (_value: string) => void;
  error?: boolean;
  disabled?: boolean;
  className?: string;
}

/** Digit slots for a one-time password. */
export const OtpInput = React.forwardRef<HTMLDivElement, OtpInputProps>(
  (
    { length = 6, value, onChange, onComplete, error = false, disabled = false, className },
    ref,
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);
    const [localValues, setLocalValues] = React.useState<string[]>(new Array(length).fill(''));
    const slotIds = React.useMemo(
      () => Array.from({ length }, (_, slot) => `otp-slot-${slot}`),
      [length],
    );

    /** Pad/truncate the parent string into one local value per slot. */
    React.useEffect(() => {
      const values = value.split('').slice(0, length);
      const paddedValues = [...values, ...new Array(length - values.length).fill('')];
      setLocalValues(paddedValues);
    }, [value, length]);

    const focusInput = (index: number) => {
      if (inputRefs.current[index]) {
        inputRefs.current[index]?.focus();
      }
    };

    /** Keep the last digit, emit the joined value, and advance focus. */
    const handleChange = (index: number, inputValue: string) => {
      if (disabled) return;

      const digit = inputValue.replace(/\D/g, '').slice(-1);

      const newValues = [...localValues];
      newValues[index] = digit;
      setLocalValues(newValues);

      const newValue = newValues.join('');
      onChange(newValue);

      if (digit && index < length - 1) {
        focusInput(index + 1);
      }

      if (newValue.length === length && onComplete) {
        onComplete(newValue);
      }
    };

    /** Backspace clears/retreats; arrows, Home, and End move focus. */
    const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.key === 'Backspace') {
        e.preventDefault();
        const newValues = [...localValues];

        if (localValues[index]) {
          newValues[index] = '';
          setLocalValues(newValues);
          onChange(newValues.join(''));
        } else if (index > 0) {
          newValues[index - 1] = '';
          setLocalValues(newValues);
          onChange(newValues.join(''));
          focusInput(index - 1);
        }
      }

      if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        focusInput(index - 1);
      }

      if (e.key === 'ArrowRight' && index < length - 1) {
        e.preventDefault();
        focusInput(index + 1);
      }

      if (e.key === 'Home') {
        e.preventDefault();
        focusInput(0);
      }

      if (e.key === 'End') {
        e.preventDefault();
        focusInput(length - 1);
      }
    };

    /** Fill slots from pasted digits and fire `onComplete` when full. */
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (disabled) return;

      e.preventDefault();
      const pastedData = e.clipboardData.getData('text');

      const digits = pastedData.replace(/\D/g, '').slice(0, length);

      if (digits.length > 0) {
        const newValues = digits.split('');
        while (newValues.length < length) {
          newValues.push('');
        }

        setLocalValues(newValues);
        const newValue = newValues.join('');
        onChange(newValue);

        const nextEmptyIndex = newValues.findIndex((v) => !v);
        if (nextEmptyIndex !== -1) {
          focusInput(nextEmptyIndex);
        } else {
          focusInput(length - 1);
        }

        if (newValue.length === length && onComplete) {
          onComplete(newValue);
        }
      }
    };

    /** Select the digit so the next keystroke replaces it. */
    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      e.target.select();
    };

    return (
      <div ref={ref} className={cn('flex gap-2 justify-center', className)}>
        {localValues.map((digit, index) => (
          <input
            key={slotIds[index]}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            className={cn(
              'w-12 h-14 text-center text-2xl font-semibold rounded-xl',
              'border-2 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-2',
              'bg-[rgb(var(--card-rgb)/0.3)] backdrop-blur-[8px]',
              'text-foreground',
              error
                ? 'border-destructive focus:border-destructive focus:ring-destructive/50'
                : 'border-[color-mix(in_srgb,var(--color-1-nuance-3)_50%,transparent)] focus:border-[var(--color-2-nuance-1)] focus:ring-[var(--color-2-nuance-1)]/50',
              digit &&
                !error &&
                'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_8%,transparent)]',
              disabled && 'opacity-50 cursor-not-allowed',
              'hover:border-[color-mix(in_srgb,var(--color-1-nuance-2)_70%,transparent)]',
            )}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>
    );
  },
);

OtpInput.displayName = 'OtpInput';
